import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  Input,
  SimpleChanges,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import {debounceTime, finalize} from 'rxjs/operators';
import {FormGroup, FormBuilder, FormControl, Validators} from '@angular/forms';
import {countryConfig} from 'src/country-config/country-config';
import {emailFormatValidator} from '../../custom-validators/email-format-validator';
import {zeroValidator} from '../../custom-validators/zero-validator';
import {onlyNumbersValidator} from '../../custom-validators/only-numbers-validator';
import {passwordMatchValidator} from '../../custom-validators/password-match-validator';
import {lengthValidator} from '../../custom-validators/length-validator';
import {FormErrorValidatorService} from '../../services/form-error-validator.service';
import {SignUpFormVm} from '../../../../view-model/sign-up-form/sign-up-form.vm';
import {RegistrationExpressVm} from 'src/app/view-model/registration-express/registration-express.vm';
import {ToastEvent} from '../../event-listeners/toast/toast.event';
import {passwordFormatValidator} from '../../custom-validators/password-format-validator';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { PATTERNS } from '../../constants/pattern';
import {iDocumentTypesResponse} from 'src/app/data/networking/models/registration-express/registration-express';
import { FirebaseFlags } from 'src/app/data/networking/models/firebase/response/flags';

declare const FB: any;

@Component({
  selector: 'app-sign-up-form',
  templateUrl: './sign-up-form.component.html',
  styleUrls: ['./sign-up-form.component.scss'],
  providers: [FormBuilder],
})
export class SignUpFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() idPrefix = '';
  @Input() isModal = false;
  @Input() customerData;
  @Input() formComplete = true;
  @Output() signedUp = new EventEmitter<any>();
  @Output() openLogin = new EventEmitter<any>();
  @Output() emailErrorRegister = new EventEmitter<any>();
  phoneLength = countryConfig.phoneLength;
  signUpForm: FormGroup;
  passwordsForm: FormGroup;
  isSocialNetwork = false;
  countryConfig = countryConfig;
  isLoading = {
    facebook: false,
    google: false,
    email: false,
    apple: false,
  };
  activateSmsValidationForm = false;
  error = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    country: '',
    phone: '',
    gender: '',
    documentNumber: '',
    token: '',
  };
  formConfig = {
    minPassword: 6,
    maxPassword: 64,
    maxToken: 1500,
    phoneLength: this.phoneLength,
    email: 320,
    documentNumber: 20,
    name: 30,
  };
  errorObj: any;
  phone: any;
  inputPass = true;
  errorEmail: any;

  documentTypes: iDocumentTypesResponse[];
  modalOkSingUp = {open: false};
  ObservableSubs:Subscription;
  typeOfRegister: string = '';
  idCustomerWebSafeAnonymous = '';
  subscriptionList: Subscription[] = [];
  authActiveConfig: FirebaseFlags['authActiveConfig'];

  get defaultPhoneCode(): string {
    return (countryConfig.phoneCode.length > 0) ? countryConfig.phoneCode[0] : '';
  }

  constructor(
    private formBuilder: FormBuilder,
    private fev: FormErrorValidatorService,
    private vm: SignUpFormVm,
    private toastEvent: ToastEvent,
    private activatedRoute: ActivatedRoute,
    private registrationExpressVm: RegistrationExpressVm
  ) {}
  
  ngOnDestroy(): void {
    this.ObservableSubs.unsubscribe();
    this.subscriptionList?.forEach(subscription => subscription?.unsubscribe());
  }

  setLoading(provider) {
    this.isLoading = {
      facebook: false,
      google: false,
      email: false,
      apple: false,
    };

    switch (provider) {
      case 'GOOGLE':
        this.isLoading.google = true;
        return;
      case 'FACEBOOK':
        this.isLoading.facebook = true;
        return;
      case 'EMAIL':
        this.isLoading.email = true;
        return;
      case 'APPLE':
        this.isLoading.apple = true;
        return;
      default:
        this.isLoading.email = false;
        break;
    }
  }

  initForm(customer?) {
    this.isSocialNetwork = !!customer;
    this.setLoading(null);
    this.errorObj = '';
    this.getDocumentTypes();
    this.passwordsForm = this.formBuilder.group(
      {
        password: new FormControl(customer ? customer.password : '', [
          Validators.required,
          passwordFormatValidator,
        ]),
        confirmPassword: new FormControl(customer ? customer.password : '', [
          Validators.required,
        ]),
      },
      {validator: passwordMatchValidator},
    );

    this.signUpForm = this.formBuilder.group({
      documentNumber: new FormControl('', [
        Validators.required,
        Validators.maxLength(this.formConfig.documentNumber),
        onlyNumbersValidator,
        zeroValidator,
      ]),
      email: new FormControl(customer ? customer.email : '', [
        Validators.required,
        Validators.maxLength(this.formConfig.email),
        emailFormatValidator,
      ]),
      firstName: new FormControl('', [
        Validators.required,
        Validators.maxLength(this.formConfig.name),
        Validators.pattern(PATTERNS.ALPHANUMERIC_WITH_SPACES),
      ]),
      lastName: new FormControl('', [
        Validators.required,
        Validators.maxLength(this.formConfig.name),
        Validators.pattern(PATTERNS.ALPHANUMERIC_WITH_SPACES),
      ]),
      passwords: this.passwordsForm,
      phone: new FormControl('', this.phoneValidators),
      gender: new FormControl('female', [Validators.required, Validators.maxLength(6)]),
      countryCode: new FormControl(countryConfig.countryCode, [Validators.required, Validators.maxLength(2)]),
      registeredBy: new FormControl('EMAIL'),
      isStore: new FormControl('Web', [Validators.required]),
      terms: new FormControl(false, [ Validators.requiredTrue ])
    });

    if (countryConfig.singUpFormComponent.phoneCodeOption) {
      this.signUpForm.addControl('phoneCode', new FormControl(this.defaultPhoneCode, Validators.required));
    }
    if (countryConfig.singUpFormComponent.documentTypeOption) {
      this.signUpForm.addControl('documentTypeId', new FormControl(null, Validators.required));
    }

    this.ObservableSubs = this.signUpForm.valueChanges
      .pipe(debounceTime(500))
      .subscribe(values => {
        let spaceReg = new RegExp(" ",'g');
        let strs = this.signUpForm.get('email').value;  
        let password = this.passwordsForm.get('password').value;
        let confirmPassword = this.passwordsForm.get('confirmPassword').value;
        password = password.replace(spaceReg,"");
        confirmPassword = confirmPassword.replace(spaceReg,"");
        strs = strs.replace(spaceReg,"");
        this.signUpForm.patchValue({
          email: strs,
        })

        this.passwordsForm.patchValue({
          password: password,
          confirmPassword: confirmPassword
        })
               
        if (values.countryCode && values.phone) {
          const phoneCode = !!values.phoneCode ? values.phoneCode : '';
          this.phone = `${values.countryCode}${phoneCode}${values.phone}`;
        }
        const config = {
        ...this.formConfig,
        maxPassword:
          values.registeredBy === 'EMAIL' ? this.formConfig.maxPassword : this.formConfig.maxToken,
      };
        this.fev.signUpFormErrorValidator(values, this.signUpForm, this.error, config);
      if (values.registeredBy === 'FACEBOOK') {
      return;
      }
    });
  }

  private get phoneValidators() {
    const val = [
      Validators.required,
      onlyNumbersValidator,
      lengthValidator(this.formConfig.phoneLength),
    ];
    if (countryConfig.signUpForm.zeroValidator) {
      val.push(zeroValidator);
    }
    return val;
  }

  onSelectedCountry(event) {
    this.signUpForm.patchValue({countryCode: event.prefix});
  }
  onSelectedPhoneCode(event) {
    this.signUpForm.patchValue({phoneCode: event});
  }

  facebook() {
    this.errorObj = null;
    this.isSocialNetwork = true;
    this.setLoading('FACEBOOK');
    this.signUpForm.patchValue({registeredBy: 'FACEBOOK'});
    FB.login(
      response => {
        if (response.status === 'connected') {
          let data = {
            ...response,
            token: response.authResponse.accessToken,
          };
          FB.api('/me', {fields: 'name,email'}, res => {
            this.setLoading(null)
            data = {
              ...data,
              ...res,
            };

            const registrationData = {
              firstName: data.name,
              email: data.email,
              passwords: {
                password: data.token,
                confirmPassword: data.token,
              },
              token: data.token,
            };
            this.signUpForm.patchValue(registrationData);
          }, error => {
            this.setLoading(null)

            data = {
              ...data,
              ...error,
            }; 

            if (data.email && data.token) {
              const registrationData = {
                firstName: data.name,
                email: data.email,
                passwords: {
                  password: data.token,
                  confirmPassword: data.token,
                },
                token: data.token,
              };
              this.signUpForm.patchValue(registrationData);
              return;
            }

            if (!data.email) {
              this.errorObj = 'Error en registro con Facebook, No se pudo obtener el correo electrónico.'
            } else {
              this.errorObj = 'Error en registro con Facebook';
            }


          });
        } else {
          this.errorObj = {
            message: 'El usuario canceló el registro o no lo autorizó completamente.',
          };
        }
      },
      {
        scope: 'email,public_profile',
        return_scopes: true,
      },
    );
  }

  google() {
    this.errorObj = null;
    this.fetchSocialNetworkData('GOOGLE');
  }

  apple() {
    this.errorObj = null;
    this.fetchSocialNetworkData('APPLE');
  }

  fillFormFromSocialNetworkData(data) {
    if (data) {
      const {credential} = data;
      const isApple = credential.providerId === 'apple.com';
      const isGoogle = credential.providerId === 'google.com';
      const profile = data.additionalUserInfo ? data.additionalUserInfo.profile : {};
      const registrationData = {
        email: isGoogle ? ( (data.user && data.user?.email) ? data.user.email : profile.email) : (isApple ? profile.email : ''),
        firstName: isGoogle ? (profile ? profile.given_name : profile.first_name) : isApple ? data.user.displayName : '',
        lastName: isGoogle ? (profile ? profile.family_name : profile.last_name) : '',
        passwords: {
          password: isGoogle ? credential.idToken : isApple ? data.user.uid : credential.accessToken,
          confirmPassword: isGoogle ? credential.idToken : isApple ? data.user.uid : credential.accessToken,
        },
        phone: data.user && data.user.phoneNumber ? data.user.phoneNumber : '',
        token: isGoogle ? credential.idToken : credential.accessToken,
      };
      this.signUpForm.patchValue(registrationData);
    }
  }

  fetchSocialNetworkData(provider) {
    this.setLoading(provider);
    this.isSocialNetwork = true;
    this.signUpForm.patchValue({registeredBy: provider});
    this.vm
      .fetchFirebaseData(this.signUpForm.value)
      .pipe(finalize(() => this.setLoading(null)))
      .subscribe(
        (response) => {
          if (response && (response.idCustomerWebSafe || response.id > 0)) {
            this.signedUp.emit(response);
            return;
          }
          this.fillFormFromSocialNetworkData(response);
          this.setLoading(null);
        },
        (error) => {
          this.errorObj = error;
        },
      );
  }

  validateCustomer() {
    this.signUpForm.patchValue({
      email: this.signUpForm.get('email').value.toLowerCase()
    });    
    if (
      this.signUpForm.valid
      && !this.isLoading.facebook
      && !this.isLoading.email
      && !this.isLoading.google
      && !this.isLoading.apple
    ) {
      this.errorObj = null;
      this.setLoading('EMAIL');

      this.vm
        .validateCustomer(this.signUpForm.value)
        .pipe(finalize(() => this.setLoading(null)))
        .subscribe(
          response => {
            if (response && response.confirmation) {
              if (countryConfig.singUpFormComponent.directSingUp) {
                this.signUp();
              } else {
                this.activateSmsValidationForm = true;
                return;
              }
            }
            const email = this.signUpForm.value.email;
            this.vm.getCustomerOrigin( {email: email} ).subscribe( validation => {
              const {confirmation, message} = response;
              const {origin, status, statusCode} = validation;
              this.errorEmail = {confirmation, message, origin, status, statusCode, email}
              this.emailErrorRegister.emit( this.errorEmail );
            });
          },
          error => {
            this.errorObj = error;
          },
        );
    }
  }

  signUp() {
    if (this.signUpForm.valid) {

      this.idCustomerWebSafeAnonymous = this.vm.getLocalStorage('customer')?.idCustomerWebSafe;

      this.setLoading('EMAIL');
      const data: any  = this.signUpForm.value;
      data.email = data.email.toLowerCase();
      this.vm
        .createCustomer(this.signUpForm.value, this.formComplete)
        .pipe(finalize(() => this.setLoading(null)))
        .subscribe(
          response => {
            if ( this.formComplete ) {
              this.signedUp.emit(response);
            } else {
              this.modalOkSingUp = {open: true};
            }
            this.vm.mergeCarts(this.idCustomerWebSafeAnonymous).subscribe();
          },
          error => {
            this.ObservableSubs.unsubscribe();
            this.toastEvent.updateToast({message: error.message, error: true});
          }
        );
    }
  }

  openLoginForm() {
    this.openLogin.emit(true);
  }

  updateForm(customerData) {
    this.initForm(customerData);
  }

  ngOnInit() {
    this.initForm(this.customerData);
    this.activatedRoute.queryParams.subscribe(params => {
      this.typeOfRegister = params && params?.singUp ? 'Tienda' : 'Web';
      if(params) this.signUpForm.patchValue(params);
    });
    this.getRemoteConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isModal) {
      if (
        changes
        && changes.openModal
        && changes.openModal.currentValue
        && changes.openModal.currentValue.open
      ) {
        this.initForm();
      }
    }

    if (this.customerData) {
      this.updateForm(this.customerData);
    }
  }

  closeActivation(event){
    if(event){
      this.activateSmsValidationForm = !event;
      const registrationData = {
        firstName: '',
        lastName: '',
        documentNumber: '',
        email: '',
        passwords: {
          password: '',
          confirmPassword: '',
        },
        gender: '',
        phone: '',
        terms: false
      };
      this.signUpForm.patchValue(registrationData);
      this.signUpForm.clearValidators();
    }
  }

  paste(e){
    e.preventDefault();
  }

  termsCheck(event){
    this.signUpForm.patchValue( {
      terms: event.target.checked
    } )
  }

  getDocumentTypes() {
    this.vm.getDocumentTypes().subscribe({
      next: response => {
        this.documentTypes = response;
        const defaultDocType = this.documentTypes.find(doc => doc.documentDefault);
        if (defaultDocType) {
          this.signUpForm.get('documentTypeId')?.setValue(defaultDocType.documentTypeId);
        }
      },
      error: error => { console.error(error); },
    });
  } 

  getRemoteConfig(): void {
    const subscription = this.vm.getRemoteConfig()
      .subscribe({
        next: (config) => {
          this.authActiveConfig = config?.authActiveConfig;
        },
        error: (error) => {
          console.error('Error fetching remote config', error);
        },
      });

    this.subscriptionList.push(subscription);
  }

  isSomeAuthActive(): boolean {
    const activeFacebook = this.authActiveConfig?.facebook;
    const activeGoogle = this.authActiveConfig?.google;
    const activeApple = this.authActiveConfig?.apple;

    return activeFacebook || activeGoogle || activeApple;
  }

  showDivider(): boolean {
    return countryConfig.singUpFormComponent.divider
      && this.formComplete
      && this.isSomeAuthActive();
  }
  
}
